import { IonContent, IonLabel, IonPage, IonSegment, IonSegmentButton, IonSpinner, useIonToast } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Header from "src/components/Header";
import { fmtFecha } from "src/components/SolicitudCapacitacionCampos";
import SolicitudGafcoreFormulario from "src/components/SolicitudGafcoreFormulario";
import { CatalogoTraslados, SolicitudTrasladoResumen, TrasladosAPI } from "src/services/traslados";

type Tab = "nueva" | "mis";

export const SituacionChip: React.FC<{ etiqueta: string; color: string }> = ({ etiqueta, color }) => (
  <span
    style={{
      background: color,
      color: "#fff",
      borderRadius: 10,
      padding: "2px 8px",
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {etiqueta}
  </span>
);

/**
 * Solicitudes a GafCORE (/gafcore/solicitudes): se entra por Solicitudes >
 * GafCORE > Traslados. Pestaña Nueva (traslado, pedido de activo, retiro de PS o flete) y
 * Mis solicitudes. Las atiende GAF en GafCORE; EMSYS guarda la solicitud y trae
 * su estado.
 */
const SolicitudesGafcore: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const location = useLocation();

  const [tab, setTab] = useState<Tab>(
    new URLSearchParams(location.search).get("tab") === "mis" ? "mis" : "nueva"
  );
  const [catalogo, setCatalogo] = useState<CatalogoTraslados | null>(null);
  const [mis, setMis] = useState<SolicitudTrasladoResumen[]>([]);
  const [cargando, setCargando] = useState(true);
  // null = consultando a EMSYS; false = es operario (no pide).
  const [puede, setPuede] = useState<boolean | null>(null);

  const avisar = useCallback(
    (message: string, color = "warning") => present({ message, duration: 3500, color }),
    [present]
  );

  useEffect(() => {
    TrasladosAPI.permiso()
      .then((r) => {
        const p = Boolean(r.data.data?.puede_solicitar);
        setPuede(p);
        if (!p) setTab("mis");
      })
      .catch(() => setPuede(true)); // si no se pudo consultar, decide EMSYS al enviar
  }, []);

  useEffect(() => {
    TrasladosAPI.catalogo()
      .then((r) => setCatalogo(r.data.data))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudo cargar el formulario", "danger"));
  }, [avisar]);

  const cargarMis = useCallback(() => {
    setCargando(true);
    TrasladosAPI.mis()
      .then((r) => setMis(r.data.data || []))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudieron obtener las solicitudes", "danger"))
      .finally(() => setCargando(false));
  }, [avisar]);

  useEffect(() => {
    if (tab === "mis") cargarMis();
  }, [tab, cargarMis]);

  const enviar = async (payload: Record<string, any>) => {
    try {
      const r = await TrasladosAPI.crear(payload);
      avisar(r.data.data?.info || "Solicitud enviada a GafCORE", r.data.data?.enviada === false ? "warning" : "success");
      setTab("mis");
      return true; // el formulario se desmonta al cambiar de pestaña
    } catch (err: any) {
      avisar(err?.response?.data?.error || "No se pudo enviar la solicitud", "danger");
      return false;
    }
  };

  return (
    <IonPage>
      <Header titulo="Traslados" botonIzq={{ icono: arrowBackOutline, url: "/gafcore" }} />
      <IonContent id="gafcore-solicitudes">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)} style={{ marginBottom: 16 }}>
            <IonSegmentButton value="nueva">
              <IonLabel>Nueva</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="mis">
              <IonLabel>Mis solicitudes</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {tab === "nueva" && puede === false && (
            <p style={{ color: "#555", fontSize: 14 }}>
              Los operarios no pueden cargar solicitudes a GafCORE.
            </p>
          )}

          {tab === "nueva" && puede !== false && !(puede && catalogo) && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          )}

          {tab === "nueva" && puede === true && catalogo && (
            <SolicitudGafcoreFormulario catalogo={catalogo} onEnviar={enviar} onError={(m) => avisar(m)} />
          )}

          {tab === "mis" &&
            (cargando ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <IonSpinner />
              </div>
            ) : !mis.length ? (
              <p style={{ color: "#777" }}>Todavía no enviaste solicitudes a GafCORE.</p>
            ) : (
              mis.map((s) => (
                <div
                  key={s.id}
                  onClick={() => history.push(`/gafcore/solicitudes/${s.id}`)}
                  style={{
                    cursor: "pointer",
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    padding: "10px 12px",
                    marginBottom: 8,
                    borderLeft: `5px solid ${s.situacion_color}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <strong style={{ fontSize: 14 }}>
                      N° {s.id}
                      {s.gaf_codigo ? <span style={{ color: "#777", fontWeight: 400 }}> · {s.gaf_codigo}</span> : null}
                    </strong>
                    <SituacionChip etiqueta={s.situacion_label} color={s.situacion_color} />
                  </div>
                  <div style={{ fontSize: 13, color: "#444", marginTop: 4 }}>{s.tipo_label}</div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
                    {[s.origen, s.destino].filter(Boolean).join(" → ") || "Sin lugares"}
                    {s.fecha_requerida ? ` · ${fmtFecha(s.fecha_requerida)}` : ""}
                    {s.horario ? ` ${s.horario}` : ""}
                  </div>
                </div>
              ))
            ))}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudesGafcore;
